import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Alert,
} from '@mui/material';
import StorageService from '../../../features/storageManagement/services/StorageService';
import { StorageLocation } from '../../../application/types/storageLocation';
import { LabelPrintJob } from '../types/LabelPrintJob';
import { useStorageLabelGeneration } from '../hooks/useLabelGeneration';
import { calculateLabelSheets } from '../utils/labelFormatter';
import { v4 as uuidv4 } from 'uuid';
import { useGlobalStore } from '../../../application/store/store';

interface StorageLabelSelectorProps {
    /** Callback when label print job is created */
    onJobCreated: (job: LabelPrintJob) => void;
}

/**
 * StorageLabelSelector component - allows selection of storage locations for label printing.
 *
 * Fetches storage locations via StorageService, displays selection UI, and creates
 * LabelPrintJob when user confirms selection.
 */
export const StorageLabelSelector: React.FC<StorageLabelSelectorProps> = ({
    onJobCreated,
}) => {
    const { t } = useTranslation();
    const { profile } = useGlobalStore();
    const [storageLocations, setStorageLocations] = useState<StorageLocation[]>(
        []
    );
    const [selectedStorageIds, setSelectedStorageIds] = useState<Set<string>>(
        new Set()
    );
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const storageService = new StorageService();

    // Fetch storage locations on mount
    useEffect(() => {
        const loadStorageLocations = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const fetchedLocations = await storageService.getAllStorages();
                setStorageLocations(fetchedLocations);
            } catch (err) {
                setError(t('errors.errorLoadingStorageLocations'));
                console.error('Error loading storage locations:', err);
            } finally {
                setIsLoading(false);
            }
        };

        loadStorageLocations();
    }, []);

    // Get selected storage locations
    const selectedStorages = storageLocations.filter((location) =>
        selectedStorageIds.has(location.id)
    );

    // Convert selected storage locations to label items
    const labelItems = useStorageLabelGeneration(selectedStorages);

    // Handle checkbox toggle
    const handleToggleStorage = (storageId: string) => {
        setSelectedStorageIds((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(storageId)) {
                newSet.delete(storageId);
            } else {
                newSet.add(storageId);
            }
            return newSet;
        });
    };

    // Handle select all
    const handleSelectAll = () => {
        if (selectedStorageIds.size === storageLocations.length) {
            setSelectedStorageIds(new Set());
        } else {
            setSelectedStorageIds(new Set(storageLocations.map((s) => s.id)));
        }
    };

    // Handle preview generation
    const handleGeneratePreview = () => {
        if (selectedStorages.length === 0) {
            return;
        }

        const job: LabelPrintJob = {
            id: uuidv4(),
            type: 'storage',
            items: labelItems,
            createdAt: new Date(),
            createdBy: profile?.id || '',
            totalLabels: labelItems.length,
            totalSheets: calculateLabelSheets(labelItems.length),
            status: 'previewing',
        };

        onJobCreated(job);
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {error}
            </Alert>
        );
    }

    if (storageLocations.length === 0) {
        return (
            <Alert severity="info" sx={{ m: 2 }}>
                {t('labelPrinting.noStorageLocations')}
            </Alert>
        );
    }

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {t('labelPrinting.selectStorageLocations')}
            </Typography>

            <Box display="flex" justifyContent="space-between" mb={2}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={
                                selectedStorageIds.size ===
                                    storageLocations.length &&
                                storageLocations.length > 0
                            }
                            indeterminate={
                                selectedStorageIds.size > 0 &&
                                selectedStorageIds.size <
                                    storageLocations.length
                            }
                            onChange={handleSelectAll}
                        />
                    }
                    label={t('common.selectAll')}
                />
                <Typography variant="body2" color="textSecondary">
                    {t('common.selectedCount', {
                        count: selectedStorageIds.size,
                    })}
                </Typography>
            </Box>

            <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox"></TableCell>
                            <TableCell>{t('common.locationName')}</TableCell>
                            <TableCell>{t('common.barcode')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {storageLocations.map((location) => (
                            <TableRow key={location.id} hover>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={selectedStorageIds.has(
                                            location.id
                                        )}
                                        onChange={() =>
                                            handleToggleStorage(location.id)
                                        }
                                    />
                                </TableCell>
                                <TableCell>{location.name}</TableCell>
                                <TableCell>{location.barCode || '-'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleGeneratePreview}
                    disabled={selectedStorageIds.size === 0}
                >
                    {t('labelPrinting.generatePreview')}
                </Button>
            </Box>

            {selectedStorageIds.size > 0 && (
                <Typography variant="body2" color="textSecondary" mt={2}>
                    {t('labelPrinting.sheetsRequired', {
                        count: calculateLabelSheets(selectedStorageIds.size),
                    })}
                </Typography>
            )}
        </Box>
    );
};
